import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Input,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faFolder,
  faImage,
  faVideo,
  faSearch,
  faChevronRight,
  faHome,
  faCheck,
  faSync,
  faExchangeAlt,
  faSignOutAlt,
} from "@fortawesome/free-solid-svg-icons";
import { faGoogleDrive } from "@fortawesome/free-brands-svg-icons";
import { toast } from "react-toastify";
import {
  GoogleDriveFile,
  GoogleDriveSetting,
  downloadGoogleDriveFile,
  fetchGoogleDriveConnection,
  listGoogleDriveFiles,
  unbindGoogleDrive,
} from "../../../../cores/googleDrive/connectApi";
import "./GoogleDriveFilePickerModal.scss";

type Props = {
  isOpen: boolean;
  toggle: () => void;
  companyId: string;
  mode: "image" | "video";
  onFilesSelected: (
    files: Array<{ name: string; mimeType: string; data: string; size?: number }>
  ) => void | Promise<void>;
  onSwitchAccount?: () => void;
};

type Breadcrumb = { id: string; name: string };

function getAccountInitials(name?: string, email?: string): string {
  const source = name?.trim() || email?.trim() || "?";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

export const GoogleDriveFilePickerModal = ({
  isOpen,
  toggle,
  companyId,
  mode,
  onFilesSelected,
  onSwitchAccount,
}: Props) => {
  const intl = useIntl();
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [accountBusy, setAccountBusy] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [connection, setConnection] = useState<GoogleDriveSetting | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<GoogleDriveFile[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [folderId, setFolderId] = useState<string | undefined>(undefined);
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([
    { id: "root", name: intl.formatMessage({ id: "app.googleDrive.home" }) },
  ]);

  const resetBrowseState = useCallback(() => {
    setSelectedIds(new Set());
    setSearch("");
    setSearchQuery("");
    setFolderId(undefined);
    setBreadcrumbs([
      { id: "root", name: intl.formatMessage({ id: "app.googleDrive.home" }) },
    ]);
    setError(null);
    setNextPageToken(undefined);
    setFiles([]);
  }, [intl]);

  const loadConnection = useCallback(async () => {
    try {
      const setting = await fetchGoogleDriveConnection(companyId);
      setConnection(setting);
    } catch {
      setConnection(null);
    }
  }, [companyId]);

  const loadFiles = useCallback(
    async (opts?: { append?: boolean; pageToken?: string }) => {
      setLoading(true);
      setError(null);
      try {
        const result = await listGoogleDriveFiles(companyId, {
          folderId: searchQuery ? undefined : folderId || "root",
          pageToken: opts?.pageToken,
          search: searchQuery || undefined,
        });
        setFiles((prev) =>
          opts?.append ? [...prev, ...result.files] : result.files
        );
        setNextPageToken(result.nextPageToken);
      } catch (e: any) {
        setError(
          e?.response?.data?.message ||
            e?.message ||
            intl.formatMessage({ id: "app.googleDrive.loadError" })
        );
      } finally {
        setLoading(false);
      }
    },
    [companyId, folderId, searchQuery, intl]
  );

  useEffect(() => {
    if (!isOpen) {
      resetBrowseState();
      setExporting(false);
      setAccountMenuOpen(false);
      setConnection(null);
      return;
    }
    loadConnection();
    loadFiles();
  }, [isOpen, loadFiles, loadConnection, resetBrowseState]);

  useEffect(() => {
    if (!accountMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(event.target as Node)
      ) {
        setAccountMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [accountMenuOpen]);

  const handleRefresh = () => {
    setAccountMenuOpen(false);
    resetBrowseState();
    loadFiles();
  };

  const handleUnbind = async (switchAccount: boolean) => {
    setAccountBusy(true);
    setAccountMenuOpen(false);
    try {
      await unbindGoogleDrive(companyId);
      setConnection(null);
      toast.success(intl.formatMessage({ id: "app.googleDrive.disconnected" }));
      if (switchAccount) {
        onSwitchAccount?.();
      } else {
        toggle();
      }
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ||
          intl.formatMessage({ id: "app.googleDrive.disconnectError" })
      );
    } finally {
      setAccountBusy(false);
    }
  };

  const openFolder = (file: GoogleDriveFile) => {
    setFolderId(file.id);
    setBreadcrumbs((prev) => [...prev, { id: file.id, name: file.name }]);
    setSelectedIds(new Set());
  };

  const navigateTo = (index: number) => {
    const crumb = breadcrumbs[index];
    setBreadcrumbs(breadcrumbs.slice(0, index + 1));
    setFolderId(crumb.id === "root" ? undefined : crumb.id);
    setSelectedIds(new Set());
  };

  const toggleSelect = (file: GoogleDriveFile) => {
    if (file.isFolder) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(file.id)) {
        next.delete(file.id);
      } else if (mode === "video") {
        next.clear();
        next.add(file.id);
      } else {
        next.add(file.id);
      }
      return next;
    });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(search.trim());
    setFolderId(undefined);
    setBreadcrumbs([
      { id: "root", name: intl.formatMessage({ id: "app.googleDrive.home" }) },
    ]);
  };

  const handleAccept = async () => {
    const selected = files.filter((f) => selectedIds.has(f.id));
    if (!selected.length) return;

    setExporting(true);
    setExportProgress({ done: 0, total: selected.length });
    try {
      const exported = [];
      for (const file of selected) {
        const downloaded = await downloadGoogleDriveFile(companyId, file.id);
        exported.push({
          name: downloaded.name,
          mimeType: downloaded.mimeType,
          data: downloaded.data,
          size: downloaded.size ? Number(downloaded.size) : undefined,
        });
        setExportProgress((p) => ({ ...p, done: p.done + 1 }));
      }
      await onFilesSelected(exported);
      toggle();
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          intl.formatMessage({ id: "app.googleDrive.exportError" })
      );
    } finally {
      setExporting(false);
    }
  };

  const mediaFiles = files.filter((f) => {
    if (f.isFolder) return true;
    if (mode === "video") return f.mimeType?.startsWith("video/");
    return f.mimeType?.startsWith("image/");
  });

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="google-drive-picker-modal" zIndex={2000}>
      <div className="google-drive-picker-modal__header">
        <div className="d-flex align-items-center">
          <FontAwesomeIcon icon={faGoogleDrive} className="mr-2" color="#4285F4" />
          <strong>Google Drive</strong>
        </div>
        <div className="google-drive-picker-modal__header-actions">
          <div
            className="google-drive-picker-modal__account"
            ref={accountMenuRef}
          >
            <button
              type="button"
              className="google-drive-picker-modal__account-btn"
              onClick={() => setAccountMenuOpen((open) => !open)}
              disabled={accountBusy}
              aria-expanded={accountMenuOpen}
              aria-haspopup="true"
            >
              {connection?.avatarUrl ? (
                <img
                  src={connection.avatarUrl}
                  alt=""
                  className="google-drive-picker-modal__account-avatar"
                />
              ) : (
                <span className="google-drive-picker-modal__account-avatar google-drive-picker-modal__account-avatar--initials">
                  {getAccountInitials(connection?.name, connection?.email)}
                </span>
              )}
            </button>
            {accountMenuOpen && (
              <div className="google-drive-picker-modal__account-menu">
                <div className="google-drive-picker-modal__account-info">
                  <strong>{connection?.name || connection?.email}</strong>
                  {connection?.email && connection?.name ? (
                    <span>{connection.email}</span>
                  ) : null}
                </div>
                <button type="button" onClick={handleRefresh}>
                  <FontAwesomeIcon icon={faSync} className="mr-2" />
                  {intl.formatMessage({ id: "app.googleDrive.refresh" })}
                </button>
                <button type="button" onClick={() => handleUnbind(true)}>
                  <FontAwesomeIcon icon={faExchangeAlt} className="mr-2" />
                  {intl.formatMessage({ id: "app.googleDrive.switchAccount" })}
                </button>
                <button
                  type="button"
                  className="google-drive-picker-modal__account-menu-danger"
                  onClick={() => handleUnbind(false)}
                >
                  <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
                  {intl.formatMessage({ id: "app.googleDrive.disconnect" })}
                </button>
              </div>
            )}
          </div>
          <button type="button" className="google-drive-picker-modal__close" onClick={toggle}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>

      <ModalBody>
        <form onSubmit={handleSearch} className="google-drive-picker-modal__search mb-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={intl.formatMessage({ id: "app.googleDrive.searchPlaceholder" })}
          />
          <Button type="submit" color="light">
            <FontAwesomeIcon icon={faSearch} />
          </Button>
        </form>

        <div className="google-drive-picker-modal__breadcrumbs mb-3">
          {breadcrumbs.map((crumb, index) => (
            <span key={crumb.id}>
              {index > 0 && (
                <FontAwesomeIcon icon={faChevronRight} className="mx-2 text-muted" size="xs" />
              )}
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={() => navigateTo(index)}
              >
                {index === 0 ? <FontAwesomeIcon icon={faHome} className="mr-1" /> : null}
                {crumb.name}
              </button>
            </span>
          ))}
        </div>

        {exporting && (
          <div className="google-drive-picker-modal__export mb-3">
            <p className="mb-2">
              {intl.formatMessage({ id: "app.googleDrive.exporting" })}
            </p>
            <div className="progress">
              <div
                className="progress-bar"
                style={{
                  width: `${(exportProgress.done / exportProgress.total) * 100}%`,
                }}
              >
                {exportProgress.done} / {exportProgress.total}
              </div>
            </div>
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}

        {loading ? (
          <div className="text-center py-5">
            <Spinner />
          </div>
        ) : (
          <div className="google-drive-picker-modal__grid">
            {mediaFiles.map((file) => (
              <div
                key={file.id}
                className={`google-drive-picker-modal__item ${
                  selectedIds.has(file.id) ? "selected" : ""
                }`}
                onClick={() =>
                  file.isFolder ? openFolder(file) : toggleSelect(file)
                }
              >
                {selectedIds.has(file.id) && (
                  <span className="google-drive-picker-modal__check">
                    <FontAwesomeIcon icon={faCheck} />
                  </span>
                )}
                {file.thumbnailLink && !file.isFolder ? (
                  <img src={file.thumbnailLink} alt={file.name} />
                ) : (
                  <div className="google-drive-picker-modal__icon">
                    <FontAwesomeIcon
                      icon={
                        file.isFolder
                          ? faFolder
                          : file.mimeType?.startsWith("video/")
                            ? faVideo
                            : faImage
                      }
                      size="2x"
                    />
                  </div>
                )}
                <span className="google-drive-picker-modal__name">{file.name}</span>
              </div>
            ))}
            {!mediaFiles.length && (
              <p className="text-muted text-center w-100 py-4">
                {intl.formatMessage({ id: "app.googleDrive.noFiles" })}
              </p>
            )}
          </div>
        )}

        {nextPageToken && !loading && (
          <div className="text-center mt-3">
            <Button color="link" onClick={() => loadFiles({ append: true, pageToken: nextPageToken })}>
              {intl.formatMessage({ id: "app.googleDrive.loadMore" })}
            </Button>
          </div>
        )}
      </ModalBody>

      <ModalFooter className="d-flex justify-content-between">
        <span className="text-muted">
          {selectedIds.size > 0
            ? intl.formatMessage(
                { id: "app.googleDrive.selectedCount" },
                { count: selectedIds.size }
              )
            : null}
        </span>
        <div>
          {selectedIds.size > 0 && (
            <Button color="link" onClick={() => setSelectedIds(new Set())} className="mr-2">
              {intl.formatMessage({ id: "app.googleDrive.clear" })}
            </Button>
          )}
          <Button
            color="primary"
            onClick={handleAccept}
            disabled={!selectedIds.size || exporting}
          >
            {intl.formatMessage({ id: "app.googleDrive.accept" })}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};
